"use client";

import posthog from "posthog-js";

export type PosthogIdentifiableSession = {
  user: {
    id: string;
    email?: string | null;
  };
  profile?: {
    email?: string | null;
  } | null;
};

const resolveEmail = (session: PosthogIdentifiableSession): string | null => {
  if (typeof session.profile?.email === "string" && session.profile.email) {
    return session.profile.email;
  }
  if (typeof session.user.email === "string" && session.user.email) {
    return session.user.email;
  }
  return null;
};

const ensurePosthogInitialized = async (): Promise<void> => {
  try {
    const { initializePostHog } = await import("@/lib/analytics/posthog-lazy");
    initializePostHog();
  } catch {
    // Analytics should never block UI flows.
  }
};

export const identifyPosthogUser = async ({
  session,
}: {
  session: PosthogIdentifiableSession;
}): Promise<void> => {
  if (typeof window === "undefined") {
    return;
  }

  const userId = session.user.id;
  if (userId.trim().length === 0) {
    return;
  }

  await ensurePosthogInitialized();

  const email = resolveEmail(session);
  const personProperties = email ? { email } : undefined;

  const existingUserId = posthog.get_property("$user_id");
  if (
    typeof existingUserId === "string" &&
    existingUserId.trim().length > 0 &&
    existingUserId !== userId
  ) {
    posthog.reset();
  }

  const currentDistinctId = posthog.get_distinct_id();
  if (currentDistinctId === userId) {
    if (personProperties) {
      posthog.people.set(personProperties);
    }
    return;
  }

  posthog.identify(userId, personProperties);
};
